/**
 * Viewport Utilities
 * 
 * Functions for detecting viewport properties and mobile mode.
 */

/**
 * Check if the current viewport is mobile-sized
 */
export function isMobileViewport(): boolean {
  return window.innerWidth <= 768 || window.innerHeight <= 768;
}

/**
 * Update mobile mode visibility for UI elements
 * Note: This is a placeholder - actual DOM manipulation should be done via EventBus
 */
function updateMobileModeVisibility(): void {
  const isMobile = isMobileViewport();
  const mobileModes = document.getElementById('mobile-modes');
  const mobileSplit = document.getElementById('profile-mobile-split');
  const mobileCenter = document.getElementById('profile-mobile-center');
  const mobileFull = document.getElementById('profile-mobile-full');

  if (mobileModes && mobileSplit && mobileCenter && mobileFull) {
    const display = isMobile ? 'inline-block' : 'none';
    mobileModes.style.display = display;
    mobileSplit.style.display = display;
    mobileCenter.style.display = display;
    mobileFull.style.display = display;
  }
}

/**
 * Logo/title dimension constants (matches rendering/effects.ts and rendering/canvas.ts)
 */
const LOGO_MAX_SIZE = 56 * 1.13; // 63.28px
const LOGO_PADDING = 20;
const TEXT_FONT_SIZE = 24;
const TEXT_SPACING = 20; // Space between logo and text

/**
 * Calculate logo and title footprint
 * Returns the actual vertical space needed for logo + title section
 * 
 * DESIGN PRINCIPLE: Always assume logo will be present.
 * We know the logo dimensions from constants, so we can predict the footprint
 * without waiting for the image to load. This prevents layout shifts.
 * 
 * Uses conservative estimate: assumes square logo (aspect ratio = 1),
 * which gives maximum height. This ensures we always reserve enough space.
 */
export function computeLogoFootprint(
  logoImage: HTMLImageElement | null,
  logoLoaded: boolean
): number {
  // Use conservative estimate: assumes square logo (aspect ratio = 1),
  // which gives maximum height. This ensures we always reserve enough space.
  const logoHeight = LOGO_MAX_SIZE; // Assume square logo for maximum height
  const logoWidth = LOGO_MAX_SIZE;

  // Calculate text Y position (matches canvas.ts logic)
  // Fine-tune vertical position - move up to align better with logo, then down by 18px
  const textY = LOGO_PADDING + (logoHeight - TEXT_FONT_SIZE) / 2 - 14 + 18;

  // Calculate logo Y position (matches effects.ts drawLogo logic)
  const textYForLogo = LOGO_PADDING;
  const logoY = textYForLogo + (TEXT_FONT_SIZE - logoHeight) / 2 + 6.5;

  // Total height = max(logo bottom, text bottom) + bottom padding
  const logoBottom = logoY + logoHeight;
  const textBottom = textY + TEXT_FONT_SIZE;
  const totalHeight = Math.max(logoBottom, textBottom) + LOGO_PADDING;

  return totalHeight;
}

/**
 * Compute gutters dynamically based on logo/title footprint
 * Returns symmetrical gutters (same on all sides of an axis)
 * 
 * Design principle: Compute space from actual content, not assume static values.
 * Grid workspace = viewport - actual content footprint.
 * 
 * Always assumes logo will be present - uses predicted dimensions from constants.
 * This prevents layout shifts by computing correct gutters from the start.
 */
export function computeGutters(
  logoImage: HTMLImageElement | null,
  logoLoaded: boolean,
  viewportWidth: number,
  viewportHeight: number
): { gutterX: number; gutterY: number } {
  const isMobile = isMobileViewport();
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);

  // Base horizontal gutter from CSS
  // More aggressive: reduced default to maximize workspace
  const baseGutterX = parseFloat(
    computedStyle.getPropertyValue(isMobile ? '--gutter-x-mobile' : '--gutter-x')
  ) || (isMobile ? 20 : 80);  // Reduced from 140 to 80 for more workspace

  // Calculate logo/title footprint - always assumes logo will be present
  // Uses predicted dimensions, so no layout shift when logo loads
  const logoFootprint = computeLogoFootprint(logoImage, logoLoaded);

  // Base vertical gutter from CSS
  // More aggressive: reduced default to maximize workspace
  const baseGutterY = parseFloat(
    computedStyle.getPropertyValue(isMobile ? '--gutter-y-mobile' : '--gutter-y')
  ) || (isMobile ? 20 : 30);  // Reduced from 50 to 30 for more workspace

  // Dynamic vertical gutter: ensure logo/title doesn't overlap grid
  // Use max of base gutter or logo footprint + safety margin
  // More aggressive: reduced safety margin to maximize workspace
  const safetyMargin = 5; // Reduced from 10 to 5px for more workspace
  const minGutterY = logoFootprint + safetyMargin;
  const gutterY = Math.max(minGutterY, baseGutterY);

  // For symmetrical gutters, we use the same value on all sides
  // Horizontal: use base gutter (works well for most ratios)
  // Vertical: use computed gutter (accounts for logo)
  // For uniform gutters, we could use max(gutterX, gutterY), but that might
  // sacrifice too much space on wide monitors. Using axis-symmetrical approach.

  return {
    gutterX: baseGutterX,
    gutterY
  };
}

/**
 * Update gutters based on mobile/desktop viewport (DEPRECATED)
 * @deprecated Use computeGutters() instead for dynamic logo-aware gutters
 */
export function updateGutters(): { gutterX: number; gutterY: number } {
  const isMobile = isMobileViewport();
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);

  const gutterX = parseFloat(
    computedStyle.getPropertyValue(isMobile ? '--gutter-x-mobile' : '--gutter-x')
  ) || (isMobile ? 20 : 140);

  const gutterY = parseFloat(
    computedStyle.getPropertyValue(isMobile ? '--gutter-y-mobile' : '--gutter-y')
  ) || (isMobile ? 20 : 50);

  return { gutterX, gutterY };
}

